<template>
  <validation-observer
    v-show="isRenderAfterFullfilled"
    v-slot="observer"
    slim
  >
    <form-section
      :id="questionId || 'another-job'"
      :validated="formObserver.validated"
      v-bind="observer"
    >
      <form-row :label="`Do ${isAdditional ? 'they' : 'you'} have another job?`">
        <validation-provider
          v-slot="{ failed }"
          :data-unit-test="`${stepId}_part-time-occupation`"
          :vid="`${stepId}_part-time-occupation`"
          name="Another Job"
          rules="required"
        >
          <radio
            v-model="questionData.partTimeOccupation"
            :data-test="fieldTrackerId.partTimeOccupation"
            :name="`${stepId}.part-time-occupation`"
            :items="constants.YES_NO"
            :error="failed"
          />
        </validation-provider>
      </form-row>

      <form-section-highlight
        class="mt-12"
        :class="{ 'reset-styles': !isHighlighted, }"
      >
        <form-row
          v-if="questionData.partTimeOccupation"
          :label="titleOccupation || `What other job do ${isAdditional ? 'they' : 'you'} do?`"
          :question="formRowSubtitle"
          :help-text="`If ${isAdditional ? 'their' : 'your'} exact job isn\'t in the list, don\'t worry - please select the closest match.`"
          :error="formObserver.validated && observer.failed"
        >
          <validation-provider
            v-slot="{ errors }"
            :data-unit-test="`${stepId}_occupation-list-pt`"
            :vid="`${stepId}_occupation-list-pt`"
            name="Occupation (Additional)"
            :rules="{ occupation: { isAdditional: isAdditional } }"
          >
            <qz-autocomplete
              v-model="occupationPtModel"
              :data-test="fieldTrackerId.occupationList"
              :placeholder="helpers.getPlaceholderResponsiveText('Please select occupation', isMobile)"
              :items="occupationPtSearchItems"
              :errors="errors"
              :update-search="onOccupationSearch"
            />
          </validation-provider>
        </form-row>
        <form-row
          v-if="questionData.partTimeOccupation"
          :label="titleBusiness || `What industry is ${isAdditional ? 'their' : 'your'} other job in?`"
          :help-text="`If ${isAdditional ? 'their' : 'your'} exact business or industry isn\'t in the list, don\'t worry - please select the closest match.`"
          :error="formObserver.validated && observer.failed"
        >
          <validation-provider
            v-slot="{ errors }"
            :data-unit-test="`${stepId}_business-list-pt`"
            :vid="`${stepId}_business-list-pt`"
            name="Business (Additional)"
            :rules="businessPtRules"
          >
            <qz-autocomplete
              v-model="businessPtModel"
              :data-test="fieldTrackerId.businessList"
              :placeholder="helpers.getPlaceholderResponsiveText('Please select industry', isMobile)"
              :items="businessPtSearchItems"
              :errors="errors"
              :update-search="onBusinessSearch"
            />
          </validation-provider>
        </form-row>
      </form-section-highlight>
    </form-section>
  </validation-observer>
</template>

<script>
import QuestionBase from './Base';
import { snakeCase } from 'lodash';

export default {
  name: 'question-another-job',
  extends: QuestionBase,
  props: {
    isHighlighted: {
      type: Boolean,
      default: false,
    },
    titleOccupation: {
      type: String,
      default: null,
    },
    subtitleOccupation: {
      type: String,
      default: null,
    },
    titleBusiness: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      fields: [
        'occupationListPt',
        'occupationKeyPt',
        'businessListPt',
        'businessKeyPt',
        'partTimeOccupation',
      ],
      businessPtSearchItems: [],
      occupationPtSearchItems: [],
      employeeOccupationsRes: null,
    };
  },
  created () {
    const { occupationListPt, occupationKeyPt, businessListPt, businessKeyPt } = this.stepData;

    if (this.stepData.partTimeOccupation) {
      if (occupationListPt && occupationKeyPt) {
        this.occupationPtSearchItems.push({
          value: occupationListPt,
          text: occupationKeyPt,
        });
      }
      if (businessListPt && businessKeyPt) {
        this.businessPtSearchItems.push({
          value: businessListPt,
          text: businessKeyPt,
        });
      }
    }
  },
  computed: {
    formRowSubtitle () {
      if (this.subtitleOccupation) return this.subtitleOccupation;
      return (['van', 'motorhome'].includes(this.productName))
        ? '(e.g. Builder or Electrician)'
        : '(e.g. Teacher or sales assistant)';
    },
    occupationPtModel: {
      get () {
        return this.questionData.occupationListPt;
      },
      set ({ value, text }) {
        this.questionData.occupationListPt = value;
        this.questionData.occupationKeyPt = text;
      },
    },
    businessPtModel: {
      get () {
        return this.questionData.businessListPt;
      },
      set ({ value, text }) {
        this.questionData.businessListPt = value;
        this.questionData.businessKeyPt = text;
      },
    },
    businessPtRules () {
      return this.questionData.partTimeOccupation
        ? { businessType: { isAdditional: this.isAdditional } }
        : '';
    },
    fieldTrackerId () {
      return {
        partTimeOccupation: snakeCase(this.questionFields.partTimeOccupation),
        occupationList: snakeCase(this.questionFields.occupationListPt),
        businessList: snakeCase(this.questionFields.businessListPt),
      };
    },
  },
  methods: {
    async onOccupationSearch (value) {
      if (!value) return;

      try {
        const response = await this.getOccupationSearchResponse();

        if (response.status === 'success') {
          this.occupationPtSearchItems = response.data.map(item => ({
            value: item.id,
            text: item.name,
          }));
        }
      } catch (error) {
        console.error(error);
      }
    },
    async onBusinessSearch (value) {
      if (!value) return;

      try {
        const response = await this.$api.getBusinesses(value);
        if (response.status === 'success') {
          this.businessPtSearchItems = response.data.map(item => ({
            value: item.id,
            text: item.name,
          }));
        }
      } catch (error) {
        this.$emit('toast', {
          type: 'error',
          errors: error.toString(),
        });
      }
    },
    async getOccupationSearchResponse () {
      if (!this.employeeOccupationsRes) {
        this.employeeOccupationsRes = await this.$api.getOccupations('???');

        return this.employeeOccupationsRes;
      }

      return this.employeeOccupationsRes;
    },
  },
};
</script>

<style lang='scss' scoped>
.reset-styles {
  &.form-section-highlight {
    border-left: none;
    padding-left: 0;
  }
}
</style>
